function esc(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function csvCell(value) {
  const s = value == null ? "" : String(value);
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
}

function badge(value, colorMap) {
  const hex = colorMap[value] || "#666666";
  return `<span style="display:inline-block;padding:3px 7px;border-radius:2px;background:${hex}22;color:${hex};font-weight:700;font-size:9px;white-space:nowrap">${esc(value)}</span>`;
}

function progressBar(value, statusHex) {
  const pct = Number(value) || 0;
  const fill = statusHex || "#0D3A66";
  return `<table style="width:100%;border-collapse:collapse">
      <tr>
        <td style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#8EA5BE;width:60px;white-space:nowrap;vertical-align:middle;padding:0 10px 0 0">Progress</td>
        <td style="vertical-align:middle;padding:0 10px 0 0">
          <div style="background:#EEF4FB;border-radius:2.5px;height:5px;overflow:hidden">
            <div style="background:${fill};width:${pct}%;height:5px;border-radius:2.5px"></div>
          </div>
        </td>
        <td style="font-size:11px;font-weight:600;color:#334155;width:35px;text-align:right;white-space:nowrap;vertical-align:middle;padding:0;font-variant-numeric:tabular-nums">${pct}%</td>
      </tr>
    </table>`;
}

function estimateDots(value) {
  const n = Math.max(0, Math.min(4, Number(value) || 0));
  return Array.from(
    { length: 4 },
    (_, i) =>
      `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${i < n ? "#0D3A66" : "#DCE9F5"};margin-right:3px"></span>`,
  ).join("");
}

function chip(value) {
  return `<span style="display:inline-block;font-size:10px;background:#EEF4FB;color:#1A5B9E;padding:1px 6px;border-radius:2px;font-weight:500;white-space:nowrap;margin:1px 2px 1px 0">${esc(value)}</span>`;
}

function chips(arr) {
  if (!arr || arr.length === 0)
    return `<span style="color:#8EA5BE;font-size:11px">&mdash;</span>`;
  return arr.map(chip).join("");
}

function groupByHorizon(initiatives, horizonOrder) {
  const grouped = {};
  horizonOrder.forEach((h) => {
    grouped[h] = [];
  });
  initiatives.forEach((i) => {
    if (!grouped[i.horizon]) grouped[i.horizon] = [];
    grouped[i.horizon].push(i);
  });
  return grouped;
}

function countByStatus(initiatives) {
  const counts = {};
  initiatives.forEach((i) => {
    counts[i.status] = (counts[i.status] || 0) + 1;
  });
  return counts;
}

/*
 * Unified field order: Title → Horizon → Status → Priority → Target Period →
 * Products → Impact Areas → Owner → Progress → Description [→ Estimate CSV-only]
 */

export function exportToCsv(initiatives) {
  const headers = [
    "Title",
    "Horizon",
    "Status",
    "Priority",
    "Target Period",
    "Products",
    "Impact Areas",
    "Owner",
    "Progress",
    "Description",
    "Estimate",
    "Gitlab Epic",
    "Design Link",
  ];

  const rows = initiatives.map((i) =>
    [
      i.title,
      i.horizon,
      i.status,
      i.priority,
      i.targetPeriod || "",
      (i.product || []).join(" | "),
      (i.impactAreas || []).join(" | "),
      i.owner,
      i.progress,
      i.description,
      i.estimate,
      (i.gitlabEpic || []).join(" | "),
      (i.designLink || []).join(" | "),
    ]
      .map(csvCell)
      .join(","),
  );

  const csv = [headers.map(csvCell).join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `roadmap-${today}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportToPrint(initiatives, colorMaps, horizonOrder, userName) {
  const STATUS_HEX = colorMaps.status;
  const PRIORITY_HEX = colorMaps.priority;

  const today = new Date().toLocaleDateString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Group initiatives by horizon
  const grouped = groupByHorizon(initiatives, horizonOrder || []);
  const populated = (horizonOrder || []).filter(
    (h) => (grouped[h] || []).length > 0,
  );

  // Summary strip — Total + up to 4 most common statuses
  const statusCounts = countByStatus(initiatives);
  const summaryStatuses = Object.keys(statusCounts)
    .sort((a, b) => statusCounts[b] - statusCounts[a])
    .slice(0, 4);
  const allStatCells = [null, ...summaryStatuses];

  const summaryStrip = `
    <table style="width:100%;border-collapse:collapse;border-bottom:1px solid #DCE9F5">
      <tr>
        ${allStatCells
          .map((status, idx) => {
            const isLast = idx === allStatCells.length - 1;
            const count =
              status === null ? initiatives.length : statusCounts[status];
            const label = status === null ? "Total" : status;
            const hex =
              status === null ? "#0D3A66" : STATUS_HEX[status] || "#64748B";
            return `<td style="text-align:center;padding:18px 20px 16px;${isLast ? "" : "border-right:1px solid #DCE9F5;"}">
              <div style="font-size:28px;font-weight:700;color:${hex};line-height:1;font-variant-numeric:tabular-nums">${count}</div>
              <div style="font-size:9.5px;text-transform:uppercase;letter-spacing:0.12em;color:#8EA5BE;margin-top:5px">${esc(label)}</div>
            </td>`;
          })
          .join("")}
      </tr>
    </table>`;

  // Per-initiative card
  const initiativeCard = (i) => {
    const statusHex = STATUS_HEX[i.status] || "#64748B";
    const gitlabLinks = i.gitlabEpic || [];
    const designLinks = i.designLink || [];
    const hasLinks = gitlabLinks.length > 0 || designLinks.length > 0;

    const linksHtml = !hasLinks
      ? ""
      : `<div style="margin-top:10px">
          ${[
            ...gitlabLinks.map(
              (url) =>
                `<span style="margin-right:16px;white-space:nowrap"><span style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#8EA5BE;margin-right:4px">GitLab</span><a href="${esc(url)}" style="font-size:10.5px;color:#1A5B9E;border-bottom:1px solid #B8D4EE;text-decoration:none">${esc(url)}</a></span>`,
            ),
            ...designLinks.map(
              (url) =>
                `<span style="margin-right:16px;white-space:nowrap"><span style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#8EA5BE;margin-right:4px">Design</span><a href="${esc(url)}" style="font-size:10.5px;color:#1A5B9E;border-bottom:1px solid #B8D4EE;text-decoration:none">${esc(url)}</a></span>`,
            ),
          ].join("")}
        </div>`;

    const descHtml = i.description
      ? `<div style="font-size:11.5px;color:#4A6380;line-height:1.65;padding-top:10px;border-top:1px solid #EEF4FB">${esc(i.description)}</div>`
      : "";

    return `<div class="init-card" style="border:1px solid #DCE9F5;margin-bottom:10px;overflow:hidden">
        <table style="width:100%;border-collapse:collapse">
          <tr>
            <td style="width:4px;background:${statusHex};padding:0">&nbsp;</td>
            <td style="padding:0;vertical-align:top">
              <table style="width:100%;border-collapse:collapse;background:#FAFCFF;border-bottom:1px solid #DCE9F5">
                <tr>
                  <td style="padding:13px 16px 11px;vertical-align:top">
                    <span style="font-size:13.5px;font-weight:600;color:#1A2B3C;line-height:1.35">${esc(i.title)}</span>
                  </td>
                  <td style="padding:13px 16px 11px;text-align:right;vertical-align:top;white-space:nowrap">
                    ${badge(i.status, STATUS_HEX)}&nbsp;${badge(i.priority, PRIORITY_HEX)}
                  </td>
                </tr>
              </table>
              <div style="padding:13px 16px 14px">
                <table style="width:100%;border-collapse:collapse;margin-bottom:11px">
                  <tr>
                    <td style="width:20%;padding:0 16px 0 0;vertical-align:top">
                      <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.13em;color:#8EA5BE;margin-bottom:3px">Owner</div>
                      <div style="font-size:12px;color:#334155">${esc(i.owner)}</div>
                    </td>
                    <td style="width:20%;padding:0 16px 0 0;vertical-align:top">
                      <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.13em;color:#8EA5BE;margin-bottom:3px">Target Period</div>
                      <div style="font-size:12px;color:#334155">${esc(i.targetPeriod || "—")}</div>
                    </td>
                    <td style="width:20%;padding:0 16px 0 0;vertical-align:top">
                      <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.13em;color:#8EA5BE;margin-bottom:3px">Estimate</div>
                      <div style="margin-top:3px">${estimateDots(i.estimate)}</div>
                    </td>
                    <td style="width:20%;padding:0 16px 0 0;vertical-align:top">
                      <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.13em;color:#8EA5BE;margin-bottom:3px">Products</div>
                      <div>${chips(i.product)}</div>
                    </td>
                    <td style="width:20%;padding:0;vertical-align:top">
                      <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.13em;color:#8EA5BE;margin-bottom:3px">Impact Areas</div>
                      <div>${chips(i.impactAreas)}</div>
                    </td>
                  </tr>
                </table>
                <div style="margin-bottom:11px">${progressBar(i.progress, statusHex)}</div>
                ${descHtml}
                ${linksHtml}
              </div>
            </td>
          </tr>
        </table>
      </div>`;
  };

  // Horizon sections
  const sectionsHtml = populated
    .map((horizonName) => {
      const items = grouped[horizonName];
      return `<div class="horizon-section" style="margin-top:30px">
        <div style="padding-bottom:9px;border-bottom:2px solid #0D3A66;margin-bottom:14px">
          <span style="font-family:Georgia,'Times New Roman',serif;font-size:15px;font-weight:normal;color:#0D3A66;letter-spacing:0.02em">${esc(horizonName)}</span>
          <span style="font-size:10.5px;color:#8EA5BE;background:#EEF4FB;padding:2px 8px;border-radius:10px;font-variant-numeric:tabular-nums;margin-left:10px">${items.length} initiative${items.length !== 1 ? "s" : ""}</span>
        </div>
        ${items.map(initiativeCard).join("")}
      </div>`;
    })
    .join("");

  const userLine = userName
    ? `<div style="font-size:11px;color:#7EB3D8;line-height:1.9">By ${esc(userName)}</div>`
    : "";

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>VeroSource Roadmap</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
  body { font-family: 'Segoe UI', -apple-system, system-ui, sans-serif; background: #EEF4FB; color: #334155; }
  .page { max-width: 1000px; margin: 0 auto; background: #FFFFFF; box-shadow: 0 2px 16px rgba(13,58,102,0.10); }
  .init-card { page-break-inside: avoid; }
  .horizon-section { page-break-before: auto; }
  @page { margin-top: 12mm; margin-bottom: 0; margin-left: 15mm; margin-right: 15mm; size: landscape; }
  @media print {
    body { background: white; }
    .page { max-width: none; box-shadow: none; margin: 0; }
  }
</style>
</head>
<body>
<div class="page">
  <div style="background:#0D3A66;padding:28px 40px 26px;-webkit-print-color-adjust:exact;color-adjust:exact;print-color-adjust:exact">
    <table style="width:100%;border-collapse:collapse">
      <tr>
        <td style="vertical-align:bottom;padding:0">
          <div style="font-size:9.5px;letter-spacing:0.22em;text-transform:uppercase;color:#7EB3D8;margin-bottom:8px">VeroSource</div>
          <div style="font-family:Georgia,'Times New Roman',serif;font-size:26px;font-weight:normal;color:#FFFFFF;line-height:1.2">Product Roadmap</div>
        </td>
        <td style="vertical-align:bottom;text-align:right;padding:0">
          <div style="font-size:11px;color:#7EB3D8;line-height:1.9"><strong style="color:#C8DFF2;font-weight:600">${initiatives.length} Initiative${initiatives.length !== 1 ? "s" : ""}</strong></div>
          <div style="font-size:11px;color:#7EB3D8;line-height:1.9">Exported ${today}</div>
          ${userLine}
        </td>
      </tr>
    </table>
  </div>
  ${summaryStrip}
  <div style="padding:0 40px 36px">
    ${sectionsHtml}
  </div>
  <table style="width:100%;border-collapse:collapse;border-top:1px solid #DCE9F5">
    <tr>
      <td style="padding:14px 40px;font-size:9.5px;color:#94A3B8;letter-spacing:0.05em;vertical-align:middle">
        <span style="font-weight:700;color:#0D3A66;letter-spacing:0.16em;text-transform:uppercase">VeroSource</span>
      </td>
      <td style="padding:14px 0;text-align:center;font-size:9.5px;color:#94A3B8;letter-spacing:0.05em;vertical-align:middle">
        ${initiatives.length} initiative${initiatives.length !== 1 ? "s" : ""}
      </td>
      <td style="padding:14px 40px;text-align:right;font-size:9.5px;color:#94A3B8;letter-spacing:0.05em;vertical-align:middle">
        Confidential &middot; ${today}
      </td>
    </tr>
  </table>
</div>
</body>
</html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.onload = () => win.print();
}
